/**
 * /mfa/challenge — second-factor entry after a password-only login.
 *
 * The route expects ``location.state.email`` set by the Login page.
 * If the user lands here without state (e.g. by typing the URL),
 * we redirect back to /login.
 */

import { type FormEvent, useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { authApi, type AuthApiError } from "../../auth/api";

interface LocationState {
  email?: string;
  from?: string;
}

export function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const state = (location.state as LocationState | null) ?? {};
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!state.email) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!state.email) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (useRecovery) {
        await authApi.mfaRecovery(state.email, code);
      } else {
        await authApi.mfaChallenge(state.email, code);
      }
      await refresh();
      navigate(state.from ?? "/", { replace: true });
    } catch (exc) {
      const apiError = exc as AuthApiError;
      setError(
        apiError.status === 401
          ? useRecovery
            ? "Recovery code did not match."
            : "That code is not valid."
          : "Verification failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1 className="auth-title">Two-factor authentication</h1>
        <p className="auth-subtle">
          {useRecovery
            ? "Enter one of your recovery codes."
            : "Enter the six-digit code from your authenticator app."}
        </p>
        <label className="auth-field">
          <span className="auth-label">
            {useRecovery ? "Recovery code" : "Code"}
          </span>
          <input
            type="text"
            autoComplete="one-time-code"
            inputMode={useRecovery ? "text" : "numeric"}
            pattern={useRecovery ? undefined : "[0-9]{6}"}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </label>
        {error !== null && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? "Verifying..." : "Verify"}
        </button>
        <div className="auth-links">
          <button
            type="button"
            className="auth-link"
            onClick={() => {
              setUseRecovery((current) => !current);
              setCode("");
              setError(null);
            }}
          >
            {useRecovery
              ? "Use authenticator code instead"
              : "Use a recovery code instead"}
          </button>
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
